/*
 * Zarafae toolbar (sections/zarafae-toolbar.liquid; critique item mobile-single-bottom-slot).
 *
 * Every slot is a plain link, so the bar works before this script loads. The script adds:
 *   - Search opens the header's search dialog (#search-modal) instead of loading the search page.
 *   - Cart opens the cart drawer (#cart-drawer) when the store uses one.
 *   - Sizes (product pages) clicks the page's size chart button, else follows the size guide link;
 *     with neither, the slot is hidden.
 *   - The cart count and the "Cart, N items" name follow cart updates, like the header cart icon.
 *   - body[data-zf-kb] while the on-screen keyboard is open, so both bottom bars step aside.
 * The buy bar handover (body[data-zf-buybar]) is set by assets/sticky-add-to-cart.js; the slide is CSS.
 *
 * Editor-safe: document and window listeners are added once behind window.__zfToolbar, and each
 * <zf-toolbar> syncs itself when it connects, so theme editor section reloads re-initialise.
 */
import { StandardEvents } from '@shopify/events';

/** The keyboard counts as open when the visual viewport is this many pixels shorter than the window. */
const KEYBOARD_GAP = 150;

/** Editable fields that raise the on-screen keyboard. */
const TYPING_FIELDS =
  'input:not([type="checkbox"], [type="radio"], [type="button"], [type="submit"], [type="reset"], [type="range"], [type="color"], [type="file"]), textarea, [contenteditable]:not([contenteditable="false"])';

class ZfToolbar extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.syncFromStorage();
    this.#hideEmptySizes();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  /**
   * Shows the cart count (hidden at 0, "99+" above 99) and updates the slot's accessible name.
   * @param {number} count - Items in the cart.
   */
  renderCount(count) {
    const badge = this.querySelector('[data-zf-toolbar-count]');
    const cart = /** @type {HTMLElement | null} */ (this.querySelector('[data-zf-toolbar-cart]'));
    if (!badge || !cart) return;

    badge.textContent = count > 99 ? '99+' : String(count);
    badge.toggleAttribute('hidden', count === 0);

    const template = count === 1 ? cart.dataset.zfNameOne : cart.dataset.zfNameOther;
    if (template) cart.setAttribute('aria-label', template.replace('[count]', String(count)));
  }

  /** Uses a count the header cart icon stored in the last 10 seconds (assets/cart-icon.js). */
  syncFromStorage() {
    try {
      const stored = sessionStorage.getItem('cart-count');
      if (!stored) return;

      const { value, timestamp } = JSON.parse(stored);
      const count = parseInt(value, 10);
      if (Date.now() - timestamp < 10000 && count >= 0) this.renderCount(count);
    } catch (_) {
      // Storage blocked or unreadable: keep the server-rendered count.
    }
  }

  /** A product page with neither a size chart button nor a size guide link has no Sizes slot. */
  #hideEmptySizes() {
    const sizes = /** @type {HTMLElement | null} */ (this.querySelector('[data-zf-toolbar-sizes]'));
    if (!sizes) return;

    const hasChart = Boolean(document.querySelector('main [data-zf-size-chart-open]'));
    sizes.closest('li')?.toggleAttribute('hidden', !hasChart && !sizes.dataset.zfFallback);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const slot = /** @type {HTMLElement | null} */ (event.target.closest('a, button'));
    if (!slot || !this.contains(slot)) return;

    if (slot.matches('[data-zf-toolbar-search]')) {
      const dialog = /** @type {any} */ (document.getElementById('search-modal'));
      if (typeof dialog?.showDialog === 'function') {
        event.preventDefault();
        dialog.showDialog();
      }
    } else if (slot.matches('[data-zf-toolbar-cart][data-zf-drawer]')) {
      const drawer = /** @type {any} */ (document.getElementById('cart-drawer'));
      if (typeof drawer?.open === 'function') {
        event.preventDefault();
        drawer.open();
      }
    } else if (slot.matches('[data-zf-toolbar-sizes]')) {
      const chart = /** @type {HTMLElement | null} */ (document.querySelector('main [data-zf-size-chart-open]'));
      if (chart) {
        chart.click();
      } else if (slot.dataset.zfFallback) {
        window.location.assign(slot.dataset.zfFallback);
      }
    }
  };
}

if (!(/** @type {any} */ (window).__zfToolbar)) {
  /** @type {any} */ (window).__zfToolbar = true;

  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    /** @type {any} */ (event).promise
      ?.then(({ cart, detail }) => {
        const count = cart?.totalQuantity ?? detail?.itemCount ?? 0;
        document.querySelectorAll('zf-toolbar').forEach((toolbar) => {
          /** @type {ZfToolbar} */ (toolbar).renderCount(count);
        });
      })
      .catch(() => {
        // The header cart icon reports failed updates; the toolbar keeps its last count.
      });
  });

  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    document.querySelectorAll('zf-toolbar').forEach((toolbar) => {
      /** @type {ZfToolbar} */ (toolbar).syncFromStorage();
    });
  });

  const viewport = window.visualViewport;
  if (viewport) {
    // Pinch zoom also shrinks the visual viewport, so compare at scale 1 and require a focused field.
    const updateKeyboard = () => {
      const active = document.activeElement;
      const typing = active instanceof HTMLElement && active.matches(TYPING_FIELDS);
      const shrunk = viewport.height * viewport.scale < window.innerHeight - KEYBOARD_GAP;
      document.body.toggleAttribute('data-zf-kb', typing && shrunk);
    };

    viewport.addEventListener('resize', updateKeyboard);
    document.addEventListener('focusout', () => requestAnimationFrame(updateKeyboard));
  }
}

if (!customElements.get('zf-toolbar')) {
  customElements.define('zf-toolbar', ZfToolbar);
}
